/**
 * Silent-but-attached game-audio contract (AUDIO-BUG-12009).
 *
 * Field evidence: guests receive ~128 kbps Opus while audioLevel stays 0 because the host
 * captures AudioCaster with no game content rendering into it. Bitrate alone is not health.
 */

/** Inbound audio level at or below this is treated as silence (WebRTC 0–1 scale). */
export const SILENT_AUDIO_LEVEL_MAX = 0.01;

/**
 * Bitrate (bps) at or above this means an audio track is attached and encoding.
 * Comfort-noise / silence Opus sits near 100–130 kbps in field sessions.
 */
export const HEALTHY_AUDIO_BITRATE_MIN_BPS = 20_000;

/** How long silence + healthy bitrate must hold before the host acts. */
export const SILENT_AUDIO_DWELL_MS = 8_000;

/** Minimum gap between automatic recapture attempts for one peer. */
export const SILENT_AUDIO_RECAPTURE_COOLDOWN_MS = 30_000;

export type SilentAudioSample = {
  atMs: number;
  /** Guest inbound audioLevel (0–1). Undefined = unmeasured this tick. */
  audioLevel?: number;
  /** Guest inbound audio bitrate (bps). Undefined = unmeasured this tick. */
  audioBitrateBps?: number;
};

export type SilentAudioVerdict = 'unmeasured' | 'healthy' | 'silent-attached';

/**
 * Classify finite audio readings using an inclusive silence ceiling (WebRTC 0–1
 * level) and bitrate floor (bps). Missing, nonfinite, or below-floor bitrate readings
 * are unmeasured; a measured level above the ceiling is healthy.
 */
export function classifySilentAudioSample(
  sample: Pick<SilentAudioSample, 'audioLevel' | 'audioBitrateBps'>,
  levelMax: number = SILENT_AUDIO_LEVEL_MAX,
  bitrateMinBps: number = HEALTHY_AUDIO_BITRATE_MIN_BPS,
): SilentAudioVerdict {
  const level = sample.audioLevel;
  const bitrateBps = sample.audioBitrateBps;
  if (typeof level !== 'number' || typeof bitrateBps !== 'number') return 'unmeasured';
  if (!Number.isFinite(level) || !Number.isFinite(bitrateBps)) return 'unmeasured';
  if (level > levelMax) return 'healthy';
  if (bitrateBps < bitrateMinBps) return 'unmeasured';
  return 'silent-attached';
}

/**
 * Pure dwell evaluator. Pass the recent sample window (oldest→newest or any order).
 * Returns true when the latest measured silent-attached run spans at least dwellMs.
 * Unmeasured samples are ignored and do not break the run; healthy samples do.
 * Timestamps and dwell are in milliseconds. The input array is not reordered.
 */
export function isSilentAttachedPastDwell(
  samples: SilentAudioSample[],
  dwellMs: number = SILENT_AUDIO_DWELL_MS,
): boolean {
  const measured = samples
    .map((s) => ({ atMs: s.atMs, klass: classifySilentAudioSample(s) }))
    .filter((s) => s.klass !== 'unmeasured')
    .sort((a, b) => a.atMs - b.atMs);
  if (measured.length === 0) return false;

  let runStartMs: number | null = null;
  for (let i = measured.length - 1; i >= 0; i--) {
    if (measured[i].klass !== 'silent-attached') break;
    runStartMs = measured[i].atMs;
  }
  if (runStartMs === null) return false;
  const latest = measured[measured.length - 1];
  if (latest.klass !== 'silent-attached') return false;
  return latest.atMs - runStartMs >= dwellMs;
}
